import { useMutation, useQueryClient, MutationKey } from "react-query";
import Axios from "axios";
import { EnqueueSnackbar, useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { RemoteApiError } from "../useRemote/types";
import { TFunction } from "i18next";

export type CheckOutFleetBookingPayload = {
  bookingId: number;
  odometer?: number | null;
  conditionNotes?: string | null;
  photos?: File[];
};

/**
 * Check-out a fleet booking (end trip).
 * POST /fleet/bookings/:id/check-out
 * Multipart: odometer, conditionNotes, photos (same as check-in).
 */
export function useRemoteCheckOutFleetBooking() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation<void, RemoteApiError, CheckOutFleetBookingPayload>(
    ["fleet", "check-out-booking"] as MutationKey,
    async ({ bookingId, odometer, conditionNotes, photos }: CheckOutFleetBookingPayload) => {
      const formData = new FormData();

      if (odometer != null) formData.append("odometer", String(odometer));
      if (conditionNotes != null && conditionNotes !== "")
        formData.append("conditionNotes", conditionNotes);
      if (photos?.length) {
        photos.forEach(file => formData.append("photos", file, file.name));
      }

      await Axios.post(`/fleet/bookings/${bookingId}/check-out`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["fleet", "vehicles"]);
        enqueueSnackbar(t("fleet_checkOutCompleted"), { variant: "success" });
      },
      onError: (error: RemoteApiError) => {
        handleFleetBookingError(error, enqueueSnackbar, t);
      }
    }
  );
}

function handleFleetBookingError(
  error: RemoteApiError,
  enqueueSnackbar: EnqueueSnackbar,
  t: TFunction
) {
  const backendMessage = error.response?.data?.message ?? error.message;

  if (backendMessage.includes("You cannot complete trip after booking end time")) {
    enqueueSnackbar(t("_fleetBookingEventCannotCheckOutAfterEndTime"), { variant: "error" });
  } else if (backendMessage.includes("Vehicle must be checked out before check-in")) {
    enqueueSnackbar(t("_fleetBookingEventCannotCheckOutBeforeCheckIn"), { variant: "error" });
  } else {
    enqueueSnackbar(t("There was a server error"), { variant: "error" });
  }
}
